/**
 * CAMP AND GO SLOW custom asset — framebag product configurator.
 *
 * Pairs with blocks/custom-configurator.liquid. The Liquid renders the option
 * groups from the product's `custom.configurator_config` metafield; this
 * module owns everything dynamic:
 *
 * - live running total (variant price + selected option upcharges)
 * - the option money itself: properties are text-only, so when the build
 *   carries upcharges the submit is taken over and the bag is added together
 *   with a hidden "Bag Options" surcharge product (cart quantity = upcharge
 *   divided by that product's own price, currently $5.00 — read live from
 *   `data-surcharge-price`, never hardcoded) in one /cart/add.js call; the
 *   unit is $5 rather than $1 only to keep the cart's item count sane, since
 *   checkout shows a count we cannot theme. Both lines share a unique
 *   `_Pack` property so the cart lock machinery pairs them (no quantity
 *   edits, removing either removes both)
 * - style filtering: on products with a "Style" variant option, groups and
 *   choices carrying `data-styles` (|-joined Style values, stamped by the
 *   Liquid) only apply to those styles — switching the variant hides the
 *   rest (fields disabled so they neither validate nor submit) and deselects
 *   any now-hidden choice
 * - hidden line item property inputs (labels joined with ", " for
 *   multi-selects, each priced choice suffixed "(+$X.00)" — Casey and Greg
 *   asked for option prices on the order, matching Rockgeist's own
 *   configurator format; Sep 4 2026, reversing the earlier no-pricing rule)
 * - the aggregate `Ship timing` property and the four pre-order touchpoints
 *   (choice badge is Liquid-rendered; selection callout, summary banner, and
 *   add-to-cart relabel to "Pre-order" are handled here)
 * - (?) tooltip popovers
 *
 * Standalone vanilla module on purpose: no @theme/* imports, so it survives
 * Horizon updates unchanged.
 */

const PREORDER_PREFIX = /^pre-?order\s*[—–-]+\s*/i;
const ADD_ERROR_FALLBACK = 'Something went wrong adding your bag to the cart. Please try again or contact us.';
const ADD_INVALID_FALLBACK = 'Please complete the required options before adding to cart.';

/** Dispatch a Horizon-compatible cart:update event so drawer/bubble/page refresh. */
function dispatchCartUpdate(target, resource, sourceId, data) {
  const event = new CustomEvent('cart:update', { bubbles: true });
  // Horizon's event classes assign `detail` directly rather than using
  // CustomEvent's readonly init, so match that shape.
  Object.defineProperty(event, 'detail', {
    value: { resource, sourceId, data: { ...data } },
  });
  target.dispatchEvent(event);
}

/* ------------------------------------------------------------------ */
/* Tooltip                                                             */
/* ------------------------------------------------------------------ */

let openTooltip = null;

class CgsTooltip extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.cgs-tooltip__trigger');
    this.panel = this.querySelector('.cgs-tooltip__panel');
    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.isOpen() ? this.close() : this.open();
    });
  }

  isOpen() {
    return this.panel && !this.panel.hidden;
  }

  open() {
    if (openTooltip && openTooltip !== this) openTooltip.close();
    openTooltip = this;
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.#keepInViewport();
  }

  close() {
    if (openTooltip === this) openTooltip = null;
    // Panels can contain links now, so focus may be sitting inside the thing
    // we're about to hide — hand it back to the trigger before it vanishes.
    const focusWasInside = this.panel.contains(document.activeElement);
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (focusWasInside) this.trigger.focus();
  }

  /* Nudge the panel back on screen when it would overflow the viewport. */
  #keepInViewport() {
    // Clear the previous open's shift BEFORE measuring — measuring with it
    // still applied concludes shift = 0 and drops the correction, so an
    // edge-adjacent tooltip rendered clipped on every second open.
    this.panel.style.removeProperty('transform');
    const rect = this.panel.getBoundingClientRect();
    const margin = 8;
    let shift = 0;
    if (rect.left < margin) shift = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) shift = window.innerWidth - margin - rect.right;
    if (shift !== 0) {
      this.panel.style.transform = `translateX(calc(-50% + ${shift}px))`;
    }
  }
}

document.addEventListener('click', (event) => {
  if (openTooltip && !openTooltip.contains(event.target)) openTooltip.close();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && openTooltip) openTooltip.close();
});

if (!customElements.get('cgs-tooltip')) {
  customElements.define('cgs-tooltip', CgsTooltip);
}

/* ------------------------------------------------------------------ */
/* Configurator                                                        */
/* ------------------------------------------------------------------ */

class CgsConfigurator extends HTMLElement {
  #onVariantUpdate = null;
  #section = null;
  #adding = false;
  #invalidEl = null;

  disconnectedCallback() {
    this.#section?.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  connectedCallback() {
    this.root = this.querySelector('.cgs-configurator');
    if (!this.root) return;

    this.basePrice = Number(this.root.dataset.basePrice) || 0; // cents
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.root.dataset.currency || 'USD',
    });

    this.groups = Array.from(this.root.querySelectorAll('[data-cgs-group]'));
    this.stylePosition = Number(this.root.dataset.stylePosition) || 0; // 1-based; 0 = no Style option
    this.currentStyle = this.root.dataset.currentStyle || '';
    this.shipTimingInput = this.root.querySelector('[data-cgs-ship-timing]');
    this.bagTotalInput = this.root.querySelector('[data-cgs-bag-total]');
    this.summaryRows = this.root.querySelector('[data-cgs-summary-rows]');
    this.preorderBanner = this.root.querySelector('[data-cgs-preorder-banner]');
    this.totalEl = this.root.querySelector('[data-cgs-total]');
    this.errorEl = this.root.querySelector('[data-cgs-error]');
    this.upchargeCents = 0;

    // Any edit clears a standing validation complaint, so the message can't
    // outlive the thing it was complaining about.
    const onEdit = () => {
      this.#clearInvalid();
      this.#setError('');
      this.update();
    };
    this.root.addEventListener('change', onEdit);
    this.root.addEventListener('input', onEdit);

    // Multi-variant products (e.g. Full Triangle / Wedge as a "Style" variant)
    // change the base price via the theme's variant picker. Same event target
    // Horizon's own product-form-component uses.
    const section = this.closest('.shopify-section, dialog, product-card');
    this.#onVariantUpdate = (event) => {
      const resource = event.detail?.resource;
      if (!resource) return;
      if (typeof resource.price === 'number') {
        this.basePrice = resource.price;
      }
      if (this.stylePosition > 0) {
        const options = Array.isArray(resource.options)
          ? resource.options
          : [resource.option1, resource.option2, resource.option3];
        const style = options[this.stylePosition - 1];
        if (typeof style === 'string' && style && style !== this.currentStyle) {
          this.currentStyle = style;
          this.#applyStyle();
        }
      }
      this.update();
    };
    section?.addEventListener('variant:update', this.#onVariantUpdate);
    this.#section = section;

    // Builds with option upcharges can't go through Horizon's native add (it
    // only knows the bag line), so the configurator adds the bag + surcharge
    // together — see #addWithSurcharge. We must PREEMPT Horizon's add, and
    // Horizon fires it from the form's `submit` event via a DOCUMENT-level
    // *capture* listener (assets/component.js registerEventListeners), which
    // beats any listener on the form or its ancestors — so a submit handler
    // can't cancel it. Instead we intercept the add-to-cart button's click in
    // the capture phase: calling preventDefault there cancels the native form
    // submission outright (no submit event ⇒ Horizon never adds). The sticky
    // add-to-cart bar is covered too — it puppets this same button via
    // .click() (assets/sticky-add-to-cart.js).
    const form = document.getElementById(this.root.dataset.formId);
    if (form) {
      // $0 builds still ride Horizon's native path; keep property inputs
      // current right before it serializes the form.
      form.addEventListener('submit', () => this.update());

      const addButton = form.querySelector('button[name="add"], [type="submit"]');
      addButton?.addEventListener(
        'click',
        (event) => {
          this.update();
          // Validate on BOTH paths. A $0 build would otherwise ride Horizon's
          // native submit, which iOS blocks just as silently as we used to.
          if (!this.#validate(form)) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          if (this.upchargeCents <= 0) return; // $0 build → Horizon's native add
          // Take over: cancel Horizon's native submit before it happens.
          event.preventDefault();
          event.stopPropagation();
          this.#addWithSurcharge(form);
        },
        true // capture: run before the button's own handlers and the default action
      );
    }

    this.#applyStyle();
    this.update();
  }

  /** True when el has no data-styles filter, or its filter includes the current style. */
  #matchesStyle(el) {
    const styles = el.dataset.styles;
    if (!styles) return true;
    return styles.split('|').includes(this.currentStyle);
  }

  /**
   * Shows/hides groups and choices for the current Style variant value.
   * Hidden fields are disabled so required-validation skips them and nothing
   * submits; hidden choices are deselected so they can't linger in the order.
   * Selections that remain visible survive the switch.
   */
  #applyStyle() {
    if (this.stylePosition === 0 || !this.currentStyle) return;

    for (const groupEl of this.groups) {
      const groupVisible = this.#matchesStyle(groupEl);
      groupEl.hidden = !groupVisible;
      for (const field of groupEl.querySelectorAll(
        '[data-cgs-select], [data-cgs-check], [data-cgs-text]'
      )) {
        field.disabled = !groupVisible;
      }
      if (!groupVisible) continue;

      for (const row of groupEl.querySelectorAll('[data-cgs-choice]')) {
        const rowVisible = this.#matchesStyle(row);
        row.hidden = !rowVisible;
        const check = row.querySelector('[data-cgs-check]');
        if (check) {
          check.disabled = !rowVisible;
          if (!rowVisible) check.checked = false;
        }
      }

      const select = groupEl.querySelector('[data-cgs-select]');
      if (select) {
        let resetNeeded = false;
        for (const option of select.options) {
          if (!option.value) continue;
          const optionVisible = this.#matchesStyle(option);
          option.hidden = !optionVisible;
          option.disabled = !optionVisible;
          if (!optionVisible && option.selected) resetNeeded = true;
        }
        if (resetNeeded) select.selectedIndex = 0;
      }
    }
  }

  /**
   * Shows the description that matches a single-select's current choice.
   *
   * Per-choice copy can't live inside a native <option> (no popover, no
   * button — the OS draws the dropdown), so the block renders every choice's
   * note under the field and we reveal just the selected one. The panel scales
   * to any number of fabrics: one short paragraph, never a wall of text.
   *
   * @param {HTMLElement} groupEl
   * @param {Array<{label: string}>} items - the group's current selection
   */
  #renderChoiceNote(groupEl, items) {
    const panel = groupEl.querySelector('[data-cgs-choice-note]');
    if (!panel) return;

    // Only single-selects get this treatment; multi-selects show a (?) per row.
    const label = groupEl.dataset.groupType === 'single-select' && items.length > 0 ? items[0].label : '';

    let shown = false;
    // NB: the attribute is `data-note-for`, so the dataset key is `noteFor`.
    // A `data-cgs-` prefix here would read back as `cgsNoteFor` — that typo
    // silently hid the panel forever (nothing throws, the match just never
    // succeeds). Value-carrying attributes in this block stay unprefixed;
    // `data-cgs-*` is for marker/hook attributes we only ever query on.
    for (const note of panel.querySelectorAll('[data-note-for]')) {
      const match = label !== '' && note.dataset.noteFor === label;
      note.hidden = !match;
      if (match) shown = true;
    }
    // Nothing picked (or the pick has no copy) → collapse the panel entirely
    // rather than leaving an empty bordered strip on the page.
    panel.hidden = !shown;
  }

  /**
   * Reads one group's current selection.
   * @returns {{name: string, type: string, items: Array<{label: string, price: number, preorder: boolean, note: string}>}}
   */
  #readGroup(groupEl) {
    const result = {
      name: groupEl.dataset.groupName,
      type: groupEl.dataset.groupType,
      items: [],
    };

    if (result.type === 'single-select') {
      const select = groupEl.querySelector('[data-cgs-select]');
      const option = select && select.selectedOptions[0];
      if (option && option.value) {
        result.items.push({
          label: option.value,
          price: Number(option.dataset.price) || 0,
          preorder: option.hasAttribute('data-preorder'),
          note: option.dataset.preorderNote || '',
        });
      }
    } else if (result.type === 'multi-select') {
      for (const check of groupEl.querySelectorAll('[data-cgs-check]:checked')) {
        result.items.push({
          label: check.dataset.label,
          price: Number(check.dataset.price) || 0,
          preorder: check.hasAttribute('data-preorder'),
          note: check.dataset.preorderNote || '',
        });
      }
    } else if (result.type === 'text') {
      const input = groupEl.querySelector('[data-cgs-text]');
      const value = input ? input.value.trim() : '';
      if (value) {
        result.items.push({ label: value, price: 0, preorder: false, note: '' });
      }
    }

    return result;
  }

  update() {
    let upchargeCents = 0;
    const summary = [];
    const preorderParts = []; // {group, note}
    // Group name → property value as it must land on the order. Required
    // selects carry their property on the <select> itself (plain option
    // value), so #addWithSurcharge overrides from this map to keep the
    // "(+$X)" suffix on every priced choice, whichever control carries it.
    this.propertyValues = new Map();

    for (const groupEl of this.groups) {
      // Groups filtered out by the current Style contribute nothing.
      if (groupEl.hidden) {
        const hiddenMirror = groupEl.querySelector('[data-cgs-prop]');
        if (hiddenMirror) {
          hiddenMirror.value = '';
          hiddenMirror.disabled = true;
        }
        const hiddenCallout = groupEl.querySelector('[data-cgs-callout]');
        if (hiddenCallout) hiddenCallout.hidden = true;
        const hiddenNote = groupEl.querySelector('[data-cgs-choice-note]');
        if (hiddenNote) hiddenNote.hidden = true;
        continue;
      }

      const group = this.#readGroup(groupEl);

      // Description panel for the current choice (fabric/trim copy).
      this.#renderChoiceNote(groupEl, group.items);

      // Hidden property mirror (present unless the visible control carries
      // the property itself, i.e. required selects / required text).
      const propertyValue = group.items
        .map((item) => (item.price > 0 ? `${item.label} (+${this.formatter.format(item.price)})` : item.label))
        .join(', ');
      if (propertyValue !== '') this.propertyValues.set(group.name, propertyValue);
      const mirror = groupEl.querySelector('[data-cgs-prop]');
      if (mirror) {
        mirror.value = propertyValue;
        mirror.disabled = propertyValue === '';
      }

      // Selection-time callout (pre-order touchpoint 2)
      const callout = groupEl.querySelector('[data-cgs-callout]');
      const preorderItems = group.items.filter((item) => item.preorder);
      if (callout) {
        if (preorderItems.length > 0) {
          const note = preorderItems.map((item) => item.note).join('; ');
          callout.querySelector('[data-cgs-callout-note]').textContent = `${note}.`;
          callout.hidden = false;
        } else {
          callout.hidden = true;
        }
      }

      for (const item of preorderItems) {
        preorderParts.push({ group: group.name, note: item.note });
      }

      for (const item of group.items) {
        upchargeCents += Math.round(item.price * 100);
      }

      if (group.items.length > 0) {
        summary.push({ name: group.name, value: propertyValue });
      }
    }

    this.upchargeCents = upchargeCents;
    this.#writeShipTiming(preorderParts);
    // Per-bag list price on the order, for Rockgeist's invoicing (Greg, Sep 20).
    if (this.bagTotalInput) {
      this.bagTotalInput.value = this.formatter.format((this.basePrice + upchargeCents) / 100);
    }
    this.#renderSummary(summary, this.basePrice + upchargeCents);
    this.#renderPreorderBanner(preorderParts);
    this.#relabelAddToCart(preorderParts.length > 0);
  }

  /**
   * Adds the bag plus a companion surcharge line in one /cart/add.js call.
   * Horizon's native submit has already been cancelled by the add-to-cart
   * click interceptor in connectedCallback, so this owns the add. $0 builds
   * never reach here — they stay on the native path.
   *
   * The surcharge product carries the option money (quantity = upcharge /
   * unit price — 18 units of $5.00 for a $90 build); a unique `_Pack` on both
   * lines gives them the pack cart behavior (no quantity edits, removing
   * either removes both) for free.
   *
   * Fails closed: if the surcharge product isn't assigned in the theme
   * editor, nothing is added and an error shows — an undercharged order is
   * the one unacceptable outcome.
   */
  #addWithSurcharge(form) {
    if (this.#adding) return;

    const surchargeId = Number(this.root.dataset.surchargeVariantId) || 0;
    const unitPrice = Number(this.root.dataset.surchargePrice) || 0;
    if (!surchargeId || !unitPrice) {
      console.error(
        'cgs-configurator: no surcharge product assigned in the theme editor — refusing to add an undercharged bag.'
      );
      this.#setError(ADD_ERROR_FALLBACK);
      return;
    }

    const formData = new FormData(form);
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    // Priced choices on required selects submit the bare option value;
    // stamp the "(+$X)" form computed in update() over whatever the form sent.
    for (const [name, value] of this.propertyValues || []) {
      if (name in properties) properties[name] = value;
    }
    const packId = `cfb-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;
    properties._Pack = packId;

    const bagQuantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
    const rawQuantity = (this.upchargeCents * bagQuantity) / unitPrice;
    const surchargeQuantity = Math.round(rawQuantity);
    if (surchargeQuantity !== rawQuantity) {
      console.warn(
        `cgs-configurator: upcharge ${this.upchargeCents}¢ × ${bagQuantity} is not a whole multiple of the surcharge unit (${unitPrice}¢); charging ${surchargeQuantity} units. Keep option prices in whole dollars.`
      );
    }

    const items = [{ id: Number(formData.get('id')), quantity: bagQuantity, properties }];
    if (surchargeQuantity > 0) {
      const style = this.currentStyle ? ` — ${this.currentStyle}` : '';
      items.push({
        id: surchargeId,
        quantity: surchargeQuantity,
        properties: { _Pack: packId, For: `${this.root.dataset.productTitle}${style}` },
      });
    }

    this.#submitItems(items, bagQuantity);
  }

  async #submitItems(items, itemCount) {
    this.#adding = true;
    this.#setError(null);
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        this.#setError(result.description || result.message || ADD_ERROR_FALLBACK);
        return;
      }

      dispatchCartUpdate(this, result, this.id || 'cgs-configurator', {
        source: 'product-form-component',
        itemCount,
        productId: this.root.dataset.productId,
      });
    } catch (error) {
      console.error(error);
      this.#setError(ADD_ERROR_FALLBACK);
    } finally {
      this.#adding = false;
    }
  }

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  /**
   * Constraint validation with a message we render ourselves.
   *
   * `reportValidity()` is not enough: iOS Safari never draws the native
   * validation bubble, it only moves focus. On a phone that made an incomplete
   * build look like a dead Add to Cart button (QA, Aug 11 2026) — the add was
   * correctly refused and nothing on screen said so.
   *
   * So: name the missing field above the button, flag the field itself, and
   * scroll it to the middle of the viewport — `block: 'center'` because the
   * sticky add-to-cart bar covers the bottom of the screen.
   *
   * Returns true when the form may proceed.
   */
  #validate(form) {
    this.#clearInvalid();

    // form.elements skips disabled fields, which is what hides style-scoped
    // groups from validation (see #applyStyle).
    const invalid = Array.from(form.elements).find(
      (el) => !el.disabled && el.willValidate && !el.checkValidity()
    );
    if (!invalid) {
      this.#setError('');
      return true;
    }

    const labelEl = invalid.id ? this.root.querySelector(`label[for="${CSS.escape(invalid.id)}"]`) : null;
    // Fall back to the property name — `properties[Fabric]` reads as "Fabric".
    const raw = labelEl?.textContent || (invalid.name || '').replace(/^properties\[(.*)\]$/, '$1');
    const name = raw.replace(/\*/g, '').replace(/\s+/g, ' ').trim();

    this.#setError(name ? `Please complete “${name}” before adding to cart.` : ADD_INVALID_FALLBACK);
    this.#invalidEl = invalid;
    invalid.setAttribute('aria-invalid', 'true');
    invalid.classList.add('cgs-invalid');
    invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
    invalid.focus({ preventScroll: true });
    return false;
  }

  /** Drop the flag from a previously-invalid field. */
  #clearInvalid() {
    if (!this.#invalidEl) return;
    this.#invalidEl.removeAttribute('aria-invalid');
    this.#invalidEl.classList.remove('cgs-invalid');
    this.#invalidEl = null;
  }

  /**
   * Aggregate `Ship timing` line item property (preorder-spec §3).
   * One note: verbatim. Several: "Pre-order — Fabric ships ~Sept; Trim ships ~Oct".
   */
  #writeShipTiming(parts) {
    if (!this.shipTimingInput) return;
    if (parts.length === 0) {
      this.shipTimingInput.value = '';
      this.shipTimingInput.disabled = true;
      return;
    }
    let value;
    if (parts.length === 1) {
      value = parts[0].note;
    } else {
      const segments = parts.map(({ group, note }) => {
        const stripped = note.replace(PREORDER_PREFIX, '');
        return /^ships/i.test(stripped) ? `${group} ${stripped}` : `${group}: ${stripped}`;
      });
      value = `Pre-order — ${segments.join('; ')}`;
    }
    this.shipTimingInput.value = value;
    this.shipTimingInput.disabled = false;
  }

  #renderSummary(summary, totalCents) {
    if (this.summaryRows) {
      const rows = [{ name: 'Base price', value: this.formatter.format(this.basePrice / 100) }, ...summary];
      this.summaryRows.replaceChildren(
        ...rows.map(({ name, value }) => {
          const row = document.createElement('div');
          row.className = 'cgs-summary__row';
          const dt = document.createElement('dt');
          dt.textContent = name;
          const dd = document.createElement('dd');
          dd.textContent = value;
          row.append(dt, dd);
          return row;
        })
      );
    }
    if (this.totalEl) {
      this.totalEl.textContent = this.formatter.format(totalCents / 100);
    }
  }

  /** Banner above the total (pre-order touchpoint 3). */
  #renderPreorderBanner(parts) {
    if (!this.preorderBanner) return;
    if (parts.length === 0) {
      this.preorderBanner.hidden = true;
      return;
    }
    const notes = parts.map(({ note }) => note.replace(PREORDER_PREFIX, '')).join('; ');
    this.preorderBanner.textContent = `This is a pre-order — ${notes}`;
    this.preorderBanner.hidden = false;
  }

  /**
   * Add-to-cart button relabel (pre-order touchpoint 4). Covers BOTH the in-form
   * button and the sticky bar button.
   *
   * The two are structured differently, which is what made the old
   * `add-to-cart-component .add-to-cart-text__content span span` selector miss
   * the sticky bar entirely (QA Finding #6, Aug 10 2026): the sticky button is
   * not wrapped in an `add-to-cart-component`, and its label sits one level
   * shallower. Matching on depth is a trap — `… span span` inside the sticky
   * button resolves to the hidden `(1)` quantity span, so relabelling by that
   * selector would have corrupted the count rather than the label.
   *
   * So: take the FIRST child span of each `.add-to-cart-text__content` (the
   * label; the quantity is a later sibling) and walk down to its leaf.
   */
  #relabelAddToCart(isPreorder) {
    const section = this.closest('.shopify-section') || document;
    for (const content of section.querySelectorAll('.add-to-cart-text__content')) {
      let label = content.querySelector(':scope > span');
      if (!label) continue;
      let deeper;
      while ((deeper = label.querySelector(':scope > span'))) label = deeper;
      if (!label.dataset.cgsOriginalLabel) {
        label.dataset.cgsOriginalLabel = label.textContent.trim();
      }
      const target = isPreorder ? 'Pre-order' : label.dataset.cgsOriginalLabel;
      if (label.textContent.trim() !== target) {
        label.textContent = target;
      }
    }
  }
}

if (!customElements.get('cgs-configurator')) {
  customElements.define('cgs-configurator', CgsConfigurator);
}
